import React, { FC, useState } from 'react';
import { t } from 'i18next';

import SDaysSelector from 'Components/structural/SDaysSelector/SDaysSelector';
import UButton from 'Components/unit/UButton/UButton';
import UIconButton from 'Components/unit/UIconButton/UIconButton';
import UDatePicker from 'Components/unit/UDatePicker/UDatePicker';
import UToggle from 'Components/unit/UToggle/UToggle';

import { sharedPanelFormStyles } from '../SchedulePanel.style';

type EnabledDays = { [key: number]: boolean };

const weekDayArrayToMapping = (array: number[]) =>
  array.reduce<EnabledDays>((acc, day) => ({ ...acc, [day]: true }), {});

const weekDayMappingToArray = (enabledDays: EnabledDays) =>
  Object.keys(enabledDays)
    .map((id) => parseInt(id, 10))
    .filter((id) => enabledDays[id])
    .map((id) => id);

export type ScheduleDaysFormProps = {
  enabledDays: number[];
  disabledDays: string[];
  onEnabledDaysChange: (enabledDays: number[]) => void;
  onDisabledDaysChange: (disabledDays: string[]) => void;
};

export const ScheduleDaysForm: FC<ScheduleDaysFormProps> = ({
  enabledDays,
  disabledDays,
  onEnabledDaysChange,
  onDisabledDaysChange,
}) => {
  const [localDisabledDays, setLocalDisabledDays] = useState<(string | null)[]>(() => disabledDays);
  const hasDisabledDays = localDisabledDays.length > 0;

  const [displayDisabledDaysForm, setDisplayDisabledDaysForm] = useState<boolean>(hasDisabledDays);

  const handleEnabledDaysChange = (days: EnabledDays) => {
    onEnabledDaysChange(weekDayMappingToArray(days));
  };

  const addEmptyDisabledDay = () => setLocalDisabledDays((oldDisabledDays) => [...oldDisabledDays, null]);

  const updateDisabledDay = (index: number, date: string | null) => {
    const newDisabledDays = localDisabledDays.map((d, i) => (i === index ? date : d));
    setLocalDisabledDays(newDisabledDays);
    onDisabledDaysChange(newDisabledDays.filter(Boolean) as string[]);
  };

  const removeDisabledDay = (index: number) => {
    const newDisabledDays = localDisabledDays.filter((_, i) => i !== index);
    setLocalDisabledDays(newDisabledDays);
    onDisabledDaysChange(newDisabledDays.filter(Boolean) as string[]);
  };

  return (
    <div style={sharedPanelFormStyles.box}>
      <h4 style={sharedPanelFormStyles.title}>{t('activities:scheduling.side_panel.enabled_days')}</h4>

      <div style={{ ...sharedPanelFormStyles.element, ...sharedPanelFormStyles.daySelector }}>
        <SDaysSelector selectedDays={weekDayArrayToMapping(enabledDays)} onChange={handleEnabledDaysChange} />
      </div>

      <div style={{ ...sharedPanelFormStyles.element, display: 'flex', justifyContent: 'space-between' }}>
        {t('activities:scheduling.side_panel.disable_days')}
        <UToggle toggled={displayDisabledDaysForm} onChange={() => setDisplayDisabledDaysForm((old) => !old)} />
      </div>

      {hasDisabledDays ? (
        <div style={sharedPanelFormStyles.element}>
          {localDisabledDays.map((date, index) => (
            <div key={`${date}-${index}`} style={sharedPanelFormStyles.disabledDayContainer}>
              <div style={sharedPanelFormStyles.disabledDayDatePicker}>
                <UDatePicker
                  date={date === null ? null : new Date(date)}
                  onClick={(date: Date) => updateDisabledDay(index, date.toISOString().split('T')[0])}
                />
              </div>
              <UIconButton icon="close-light" size="S" onClick={() => removeDisabledDay(index)} ghost />
            </div>
          ))}
        </div>
      ) : null}

      {displayDisabledDaysForm ? (
        <div style={sharedPanelFormStyles.element}>
          <UButton text={t('activities:scheduling.side_panel.add_day')} onClick={addEmptyDisabledDay} />
        </div>
      ) : null}
    </div>
  );
};
